import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useTabLeader } from '@/hooks/useTabLeader';
import { useBackgroundPause } from '@/hooks/useBackgroundPause';

// Increased from 30s to 60s to reduce server load
const HEARTBEAT_INTERVAL = 60000; // 60 seconds
const IDLE_TIMEOUT = 300000; // 5 minutes

export function useActivityTracker() {
  const { data: appUser } = useCurrentAppUser();
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isActiveRef = useRef<boolean>(true);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startupDelayRef = useRef<NodeJS.Timeout | null>(null);

  // Leader election - only leader tab sends heartbeats to reduce duplicate writes
  const { isLeader } = useTabLeader('activity-tracker-leader');
  // Background detection - pause heartbeats when tab is hidden
  const { isBackground } = useBackgroundPause();

  // Start new session
  const startSession = useCallback(async () => {
    if (!appUser?.id) return;

    try {
      // Close any stale sessions first
      await supabase.rpc('close_stale_sessions');

      // Create new session
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: appUser.id,
          user_agent: navigator.userAgent,
          is_active: true,
          session_started_at: new Date().toISOString(),
          last_heartbeat_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        console.error('[ActivityTracker] Error creating session:', error);
        return;
      }

      sessionIdRef.current = data.id;

      // Update app_users online status
      await supabase
        .from('app_users')
        .update({
          is_online: true,
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', appUser.id);

      console.log('[ActivityTracker] Session started:', data.id);
    } catch (err) {
      console.error('[ActivityTracker] Failed to start session:', err);
    }
  }, [appUser?.id]);

  // Send heartbeat - only if leader AND not in background
  const sendHeartbeat = useCallback(async () => {
    // Skip heartbeat if not leader or tab is in background
    if (!isLeader || isBackground) {
      return;
    }

    if (!appUser?.id || !sessionIdRef.current) return;

    try {
      const now = new Date().toISOString();

      // Update session heartbeat
      await supabase
        .from('user_sessions')
        .update({
          last_heartbeat_at: now,
          is_active: true,
        })
        .eq('id', sessionIdRef.current);

      // Insert activity heartbeat
      await supabase
        .from('user_activity_heartbeats')
        .insert({
          session_id: sessionIdRef.current,
          user_id: appUser.id,
          current_path: location.pathname,
          is_active: isActiveRef.current,
          timestamp: now,
        });

      // Update app_users last activity
      await supabase
        .from('app_users')
        .update({
          is_online: true,
          last_activity_at: now,
        })
        .eq('id', appUser.id);
    } catch (err) {
      console.error('[ActivityTracker] Heartbeat error:', err);
    }
  }, [appUser?.id, location.pathname, isLeader, isBackground]);

  // End session
  const endSession = useCallback(async () => {
    if (!appUser?.id || !sessionIdRef.current) return;

    try {
      const now = new Date().toISOString();

      await supabase
        .from('user_sessions')
        .update({
          is_active: false,
          session_ended_at: now,
        })
        .eq('id', sessionIdRef.current);

      await supabase
        .from('app_users')
        .update({ is_online: false })
        .eq('id', appUser.id);

      console.log('[ActivityTracker] Session ended:', sessionIdRef.current);
      sessionIdRef.current = null;
    } catch (err) {
      console.error('[ActivityTracker] Failed to end session:', err);
    }
  }, [appUser?.id]);

  // Handle user activity (mouse, keyboard, etc.)
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;

    // Reset idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    idleTimeoutRef.current = setTimeout(() => {
      isActiveRef.current = false;
    }, IDLE_TIMEOUT);
  }, []);

  // Setup heartbeat interval
  useEffect(() => {
    if (!appUser?.id) return;

    // Defer session start to not block initial render
    startupDelayRef.current = setTimeout(() => {
      startSession();
    }, 1500);

    // Setup heartbeat interval - runs for all tabs but sendHeartbeat checks isLeader
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Setup activity listeners
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
      } else {
        handleActivity();
        // When becoming visible, send heartbeat immediately if leader
        if (isLeader) {
          sendHeartbeat();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle beforeunload
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page unload
      if (appUser?.id && sessionIdRef.current) {
        const url = `${import.meta.env.VITE_SUPABASE_URL || 'https://irtygpadfbavrqhldhzm.supabase.co'}/rest/v1/user_sessions?id=eq.${sessionIdRef.current}`;
        navigator.sendBeacon(
          url,
          JSON.stringify({ is_active: false, session_ended_at: new Date().toISOString() })
        );
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Cleanup startup delay if still pending
      if (startupDelayRef.current) {
        clearTimeout(startupDelayRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // End session on unmount
      endSession();
    };
  }, [appUser?.id, startSession, sendHeartbeat, handleActivity, endSession, isLeader]);

  // Track route changes - only send heartbeat if leader
  useEffect(() => {
    if (sessionIdRef.current && isLeader) {
      sendHeartbeat();
    }
  }, [location.pathname, sendHeartbeat, isLeader]);

  return {
    sessionId: sessionIdRef.current,
    isActive: isActiveRef.current,
  };
}
